/**
 * BoosterV2 Wizard - 4-step wizard with 2-column layout
 * Based on: APISAI_BOOSTER_MASTER_PACK_DEV_GRAFIKA_P0_2COL
 * 
 * PREMIUM VERSION V3 - True "hand-holding" with proper validation
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, Check } from 'lucide-react';
import PanelLayout from '@/components/PanelLayout';

import GuidancePanel from './components/GuidancePanel';
import StepGoal from './steps/StepGoal';
import StepPlaces from './steps/StepPlaces';
import StepBusiness from './steps/StepBusiness';
import StepGenerate from './steps/StepGenerate';

import { API_BASE as API } from '@/utils/api';

// Steps config
const STEPS = [
  { id: 1, title: 'Cel', fullTitle: 'Cel planu' },
  { id: 2, title: 'Miejsca', fullTitle: 'Miejsca publikacji' },
  { id: 3, title: 'Opis', fullTitle: 'Opis firmy' },
  { id: 4, title: 'Plan', fullTitle: 'Plan i generowanie' }
];

// Step explanations
const STEP_WHY = {
  1: 'Dobierzemy treści i wezwania do działania tak, żeby wspierały Twój cel biznesowy.',
  2: 'Booster tworzy treści pod miejsca, gdzie faktycznie są Twoi klienci.',
  3: 'Na podstawie opisu AI wygeneruje spersonalizowane treści dopasowane do Twojej działalności.',
  4: 'Wygenerujemy paczkę treści gotowych do publikacji. Możesz je później edytować.'
};

export default function BoosterV2Wizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { planId } = useParams();
  const { token } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  
  // Form data
  const [data, setData] = useState({
    goal: null,
    places: { fanpage: { name: '', url: '' }, groups: [] },
    business: {},
    params: { duration: 7, startOption: 'today', frequency: 5, mixedTypes: true }
  });
  
  // Load existing plan data
  useEffect(() => {
    if (planId && planId !== 'new') {
      loadPlanData();
    }
  }, [planId]);
  
  const loadPlanData = async () => {
    try {
      const res = await fetch(`${API}/api/booster/v2/campaigns/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const plan = await res.json();
        // Map plan data to form state
        setData({
          goal: plan.goal_type,
          places: plan.places || { fanpage: { name: '', url: '' }, groups: [] },
          business: {
            businessName: plan.business_name,
            businessOffer: plan.business_offer,
            targetAudience: plan.target_audience,
            location: plan.location,
            promoFocus: plan.promo_focus
          },
          params: plan.params || { duration: 7, startOption: 'today', frequency: 5, mixedTypes: true }
        });
        if (plan.current_step) setCurrentStep(plan.current_step);
      }
    } catch (err) {
      console.error('Failed to load plan:', err);
    }
  };
  
  // Save progress
  const saveProgress = async () => {
    if (!planId || planId === 'new') return;
    
    try {
      await fetch(`${API}/api/booster/v2/campaigns/${planId}/progress`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_step: currentStep,
          step_data: data
        })
      });
    } catch (err) {
      console.error('Failed to save progress:', err);
    }
  };
  
  // Validation with detailed errors
  const getValidation = () => {
    switch (currentStep) {
      case 1:
        return {
          canProceed: !!data.goal,
          error: !data.goal ? 'Wybierz cel, żeby przejść dalej' : null
        };
      case 2:
        const hasFanpage = data.places.fanpage?.name?.trim();
        const hasMinGroups = data.places.groups?.filter(g => g.isActive !== false)?.length >= 1;
        
        if (!hasFanpage) {
          return {
            canProceed: false,
            error: 'Dodaj nazwę fanpage\'a, żeby przejść dalej'
          };
        }
        if (!hasMinGroups) {
          return {
            canProceed: false,
            error: 'Dodaj minimum 1 grupę, żeby przejść dalej'
          };
        }
        return { canProceed: true, error: null };
        
      case 3:
        const hasName = data.business.businessName?.trim();
        const hasOffer = data.business.businessOffer?.trim();
        const hasAudience = data.business.targetAudience?.trim();
        const hasLocation = data.business.location?.trim();
        
        if (!hasName) {
          return {
            canProceed: false,
            error: 'Podaj nazwę firmy, żeby przejść dalej'
          };
        }
        if (!hasOffer) {
          return {
            canProceed: false,
            error: 'Opisz co oferujesz, żeby przejść dalej'
          };
        }
        if (!hasAudience) {
          return {
            canProceed: false,
            error: 'Napisz dla kogo jest Twoja oferta'
          };
        }
        if (!hasLocation) {
          return {
            canProceed: false,
            error: 'Podaj gdzie działasz, żeby przejść dalej'
          };
        }
        return { canProceed: true, error: null };
        
      case 4:
        return { canProceed: true, error: null };
        
      default:
        return { canProceed: false, error: null };
    }
  };
  
  // Dynamic "Co teraz?" based on current state
  const getWhatNow = () => {
    switch (currentStep) {
      case 1:
        return data.goal 
          ? 'Świetnie! Kliknij "Dalej" żeby przejść do miejsc publikacji.'
          : 'Wybierz główny cel Twojego planu promocji.';
      case 2:
        const hasFanpage = data.places.fanpage?.name?.trim();
        const groupCount = data.places.groups?.filter(g => g.isActive !== false)?.length || 0;
        
        if (!hasFanpage) {
          return 'Wpisz nazwę swojego fanpage\'a na Facebooku.';
        }
        if (groupCount === 0) {
          return 'Dodaj minimum 1 grupę FB, gdzie są Twoi klienci.';
        }
        if (groupCount < 5) {
          const groupWord = groupCount === 1 ? 'grupę' : (groupCount < 5 ? 'grupy' : 'grup');
          return `Masz ${groupCount} ${groupWord}. Dodaj jeszcze ${5 - groupCount}, żeby zwiększyć zasięg, lub kliknij "Dalej".`;
        }
        return 'Świetnie! Masz 5+ grup. Kliknij "Dalej" żeby przejść do opisu firmy.';
        
      case 3:
        const hasNameStep3 = data.business.businessName?.trim();
        const hasOfferStep3 = data.business.businessOffer?.trim();
        const hasAudienceStep3 = data.business.targetAudience?.trim();
        const hasLocationStep3 = data.business.location?.trim();
        const filledFieldsStep3 = [hasNameStep3, hasOfferStep3, hasAudienceStep3, hasLocationStep3].filter(Boolean).length;
        
        if (!hasNameStep3) {
          return 'Wpisz nazwę swojej firmy lub hodowli.';
        }
        if (!hasOfferStep3) {
          return 'Opisz krótko, co oferujesz klientom.';
        }
        if (!hasAudienceStep3) {
          return 'Napisz, kto jest Twoim idealnym klientem.';
        }
        if (!hasLocationStep3) {
          return 'Podaj, gdzie działasz (lokalizacja / zasięg).';
        }
        return 'Świetnie! Wszystko gotowe. Kliknij "Dalej" żeby ustawić parametry planu.';
        
      case 4:
        if (generated) {
          return 'Gotowe! Kliknij "Przejdź do planu" żeby zobaczyć wygenerowane treści.';
        }
        if (generating) {
          return 'Generuję plan... To może potrwać kilka sekund.';
        }
        return 'Sprawdź parametry i kliknij "Wygeneruj plan" żeby AI stworzyło treści.';
        
      default:
        return '';
    }
  };
  
  const validation = getValidation();
  
  // Navigation
  const goNext = async () => {
    if (!validation.canProceed) {
      toast.error(validation.error || 'Wypełnij wymagane pola');
      return;
    }
    
    await saveProgress();
    
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/panel/booster-v2-new');
    }
  };
  
  // Generate content
  const handleGenerate = async () => {
    if (!token) {
      toast.error('Sesja wygasła. Zaloguj się ponownie.');
      navigate('/login');
      return;
    }
    
    setGenerating(true);
    
    try {
      let campaignId = planId;
      
      // If this is a new campaign, create it first
      if (planId === 'new') {
        const createRes = await fetch(`${API}/api/booster/v2/campaigns/wizard`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            goal_type: data.goal,
            places: data.places,
            business_name: data.business.businessName,
            business_offer: data.business.businessOffer,
            target_audience: data.business.targetAudience,
            location: data.business.location,
            differentiator: data.business.differentiator,
            params: data.params
          })
        });
        
        if (!createRes.ok) {
          const errData = await createRes.json().catch(() => ({}));
          throw new Error(errData.detail || 'Failed to create campaign');
        }
        
        const createData = await createRes.json();
        campaignId = createData.id;
      } else {
        // Update existing campaign
        await fetch(`${API}/api/booster/v2/campaigns/${planId}`, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            goal_type: data.goal,
            business_name: data.business.businessName,
            business_offer: data.business.businessOffer
          })
        });
      }
      
      // Generate content
      const res = await fetch(`${API}/api/booster/v2/campaigns/${campaignId}/content-pack/generate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          duration: data.params.duration || 7,
          frequency: data.params.frequency || 5,
          startOption: data.params.startOption || 'today',
          mixedTypes: data.params.mixedTypes !== false
        })
      });
      
      if (res.ok) {
        setGenerated(true);
        toast.success('Plan wygenerowany! Przekierowuję do widoku planu...');
        
        // Navigate to plan view after short delay
        setTimeout(() => {
          navigate(`/panel/booster-v2-new/plan/${campaignId}`);
        }, 1500);
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.detail || 'Nie udało się wygenerować treści');
      }
    } catch (err) {
      console.error('Generate error:', err);
      toast.error(err.message || 'Błąd połączenia z serwerem');
    } finally {
      setGenerating(false);
    }
  };
  
  // Build checklist with clearer status
  const buildChecklist = () => {
    const groupCount = data.places.groups?.filter(g => g.isActive !== false)?.length || 0;
    const hasRecommendedGroups = groupCount >= 5;
    
    // Count filled business fields (4 required)
    const businessFields = [
      data.business.businessName?.trim(),
      data.business.businessOffer?.trim(),
      data.business.targetAudience?.trim(),
      data.business.location?.trim()
    ];
    const filledBusinessFields = businessFields.filter(Boolean).length;
    const allBusinessFilled = filledBusinessFields === 4;
    
    const items = [
      { 
        label: 'Cel planu', 
        status: data.goal ? 'done' : (currentStep === 1 ? 'current' : 'pending') 
      },
      { 
        label: 'Fanpage', 
        status: data.places.fanpage?.name?.trim() ? 'done' : (currentStep === 2 ? 'current' : 'pending')
      },
      { 
        label: `Grupy (${groupCount}/5)`, 
        status: hasRecommendedGroups ? 'done' : 
                groupCount >= 1 ? 'inprogress' : 
                (currentStep === 2 ? 'current' : 'pending')
      },
      { 
        label: `Opis firmy (${filledBusinessFields}/4)`, 
        status: allBusinessFilled ? 'done' : 
                filledBusinessFields > 0 ? 'inprogress' :
                (currentStep === 3 ? 'current' : 'pending')
      },
      { 
        label: 'Parametry planu', 
        status: generated ? 'done' : (currentStep === 4 && !generating ? 'current' : 'pending')
      },
      { 
        label: 'Plan wygenerowany', 
        status: generated ? 'done' : (generating ? 'current' : 'pending')
      }
    ];
    return items;
  };
  
  // Current step info
  const currentStepInfo = STEPS[currentStep - 1];
  
  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepGoal 
            value={data.goal} 
            onChange={(val) => setData({ ...data, goal: val })} 
          />
        );
      case 2:
        return (
          <StepPlaces 
            value={data.places} 
            onChange={(val) => setData({ ...data, places: val })}
            validationErrors={!validation.canProceed && currentStep === 2 ? { fanpage: validation.error } : {}}
          />
        );
      case 3:
        return (
          <StepBusiness 
            value={data.business} 
            onChange={(val) => setData({ ...data, business: val })} 
          />
        );
      case 4:
        return (
          <StepGenerate 
            value={data.params} 
            onChange={(val) => setData({ ...data, params: val })}
            onGenerate={handleGenerate}
            generating={generating}
            generated={generated}
          />
        );
      default:
        return null;
    }
  };
  
  // CTA config for guidance panel
  const getCTAConfig = () => {
    if (currentStep === 4) {
      if (generated) {
        return {
          text: 'Przejdź do planu →',
          action: () => navigate(`/panel/booster-v2-new/plan/${planId}`),
          loading: false,
          disabled: false
        };
      }
      return {
        text: 'Wygeneruj plan',
        action: handleGenerate,
        loading: generating,
        disabled: generating
      };
    }
    
    // Use fullTitle for clearer context
    const nextStepTitle = STEPS[currentStep]?.fullTitle || STEPS[currentStep]?.title || 'Zakończ';
    
    return {
      text: validation.canProceed 
        ? `Dalej: ${nextStepTitle}` 
        : 'Uzupełnij wymagane pola',
      action: goNext,
      disabled: !validation.canProceed
    };
  };
  
  const ctaConfig = getCTAConfig();
  
  return (
    <PanelLayout>
    <div className="min-h-screen bg-gray-50">
      {/* HEADER - Premium Stepper */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            {/* Back button */}
            <button 
              onClick={goBack}
              className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors font-medium"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm">Wstecz</span>
            </button>
            
            {/* Step indicator with labels */}
            <div className="flex items-center gap-1">
              {STEPS.map((step, i) => (
                <div key={step.id} className="flex items-center">
                  <div className="flex flex-col items-center">
                    {/* Circle */}
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all
                      ${currentStep > step.id 
                        ? 'bg-green-500 text-white shadow-md shadow-green-200' 
                        : currentStep === step.id
                          ? 'bg-orange-600 text-white shadow-lg shadow-orange-300 ring-4 ring-orange-100'
                          : 'bg-gray-100 text-gray-400'
                      }
                    `}>
                      {currentStep > step.id ? <Check className="w-5 h-5" /> : step.id}
                    </div>
                    {/* Label */}
                    <span className={`
                      text-xs mt-1.5 font-medium transition-colors
                      ${currentStep === step.id 
                        ? 'text-orange-700' 
                        : currentStep > step.id 
                          ? 'text-green-600' 
                          : 'text-gray-400'
                      }
                    `}>
                      {step.title}
                    </span>
                  </div>
                  {/* Connector line */}
                  {i < STEPS.length - 1 && (
                    <div className={`
                      w-16 h-0.5 mx-2 mt-[-20px] rounded-full transition-colors
                      ${currentStep > step.id ? 'bg-green-400' : 'bg-gray-200'}
                    `} />
                  )}
                </div>
              ))}
            </div>
            
            <div className="w-20" /> {/* Spacer for balance */}
          </div>
        </div>
      </div>
      
      {/* CONTENT - More spacing */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex gap-12">
          {/* Left - Work Area */}
          <div className="flex-1 min-w-0 pb-24 lg:pb-8">
            {renderStep()}
          </div>
          
          {/* Right - Guidance Panel (sticky) */}
          <div className="w-[380px] flex-shrink-0 hidden lg:block">
            <div className="sticky top-8">
              <GuidancePanel
                stepLabel={`Krok ${currentStep} z 4`}
                statusBadge={generating ? 'Generowanie...' : generated ? 'Gotowe' : 'Szkic'}
                whyText={STEP_WHY[currentStep]}
                checklist={buildChecklist()}
                whatNow={getWhatNow()}
                validationError={validation.error}
                canProceed={validation.canProceed}
                ctaText={ctaConfig.text}
                ctaAction={ctaConfig.action}
                ctaDisabled={ctaConfig.disabled}
                ctaLoading={ctaConfig.loading}
                summary={currentStep === 4 ? [
                  `${data.params.duration || 7} dni`,
                  `Start: ${data.params.startOption === 'today' ? 'dziś' : data.params.startOption === 'tomorrow' ? 'jutro' : 'za tydzień'}`,
                  `${data.params.frequency || 5} postów tygodniowo`,
                  data.params.mixedTypes !== false ? 'Różne typy treści' : 'Jednolity typ'
                ] : null}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile CTA */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
        {!validation.canProceed && validation.error && (
          <p className="text-sm text-red-600 text-center mb-2 font-medium">{validation.error}</p>
        )}
        <button
          onClick={ctaConfig.action}
          disabled={ctaConfig.disabled || ctaConfig.loading}
          className={`
            w-full py-4 font-semibold rounded-xl transition-all
            ${ctaConfig.disabled 
              ? 'bg-gray-200 text-gray-500' 
              : 'bg-gradient-to-r from-orange-600 to-orange-600 text-white shadow-lg shadow-orange-500/30'
            }
          `}
        >
          {ctaConfig.loading ? 'Przetwarzam...' : ctaConfig.text}
        </button>
      </div>
    </div>
    </PanelLayout>
  );
}
